import React, { useState } from 'react';
import { ModalsKeys } from 'global';
import { useModal } from 'hooks/useModal';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { insuranceActions, insuranceSelector } from '../../../model';
import { CoverageStatusLabels, InsuranceSelectSubmissionForm } from '../../../types';
import { InsuranceCoverageRequirements } from '../insuranceSelectSubmissionDialog/InsuranceCoverageRequirements';
import { checkIfQuillValueIsEmpty } from 'utils/quillUtils';
import { useMutation } from '@apollo/client';
import { useNotifications } from 'modules/notification';
import { coverageStatusConfig } from 'modules/vendorOpportunity/insurance/constants';
import { UPDATE_INSURANCE_QUOTE } from 'modules/vendorOpportunity/insurance/api';

export const InsuranceEnterManualLenderRequirementsDialog = () => {
  const dispatch = useDispatch();

  const insuranceQuote = useSelector(insuranceSelector.insuranceQuote);
  const insuranceBroker = useSelector(insuranceSelector.insuranceBroker);
  const insuranceCertificate = useSelector(insuranceSelector.insuranceCertificate);

  const { isOpen, handleClose } = useModal(ModalsKeys.InsuranceEnterManualLenderRequirementsDialog);
  const { handleOpen: openConfirmationModal } = useModal(ModalsKeys.Confirmation);

  const { showNotification } = useNotifications();

  const { control, getValues, handleSubmit, reset, setValue } = useFormContext<InsuranceSelectSubmissionForm>();

  const [updateInsuranceQuote, { loading }] = useMutation(UPDATE_INSURANCE_QUOTE, {
    onCompleted: () => {
      dispatch(
        insuranceActions.setInsuranceQuote({
          ...insuranceQuote,
          insuranceCoverage: {
            coverageRequirements: getValues('coverageRequirements'),
            certificateRequirements: getValues('certificateRequirements'),
            otherInstructions: getValues('otherInstructions'),
          },
        })
      );

      if (insuranceBroker) {
        dispatch(
          insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredUnverified])
        );
      }
      if (insuranceCertificate) {
        dispatch(insuranceActions.setCoverageStatusConfig(coverageStatusConfig[CoverageStatusLabels.InsuredVerified]));
      }

      showNotification('Insurance Requirements have been saved');
      handleClose();
    },
  });

  const coverageRequirements = useWatch({
    control,
    name: 'coverageRequirements',
  });

  const handleSave = (formData: InsuranceSelectSubmissionForm) => {
    openConfirmationModal({
      title: 'Save Insurance Requirements?',
      message: `These requirements and instructions can be edited later if needed.`,
      onConfirm: () => saveManualLenderRequirements(formData),
    });
  };

  const saveManualLenderRequirements = async (_formData: InsuranceSelectSubmissionForm) => {
    try {
      const insuranceCoverage = {
        coverageRequirements: getValues('coverageRequirements'),
        certificateRequirements: getValues('certificateRequirements'),
        otherInstructions: getValues('otherInstructions'),
      };
      await updateInsuranceQuote({
        variables: {
          input: {
            insuranceQuoteId: insuranceQuote?.insuranceQuoteId,
            insuranceCoverage,
          },
        },
      });
    } catch (error) {
      showNotification('Error', { type: 'error' });
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="lg" keepMounted>
      <DialogTitle>
        <Typography component="span" variant="h6">
          Select Submission
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Stack gap="1rem">
          <InsuranceCoverageRequirements />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          loading={loading}
          onClick={handleSubmit(handleSave)}
          variant="contained"
          disabled={checkIfQuillValueIsEmpty(coverageRequirements)}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
